import AdswizzReportType from '@/Enums/Adswizz/ReportType.enum';
import MapScale from '@/Enums/Map/MapScale.enum';
import ReportProperty from '@/Enums/Report/BeeswaxReportProperties.enum';
import AdswizzReportRequest from '@/Enums/ReportingDashboard/ReportRequest/AdswizzReportRequest.enum';
import AdswizzReportView from '@/Enums/ReportingDashboard/ReportView/AdswizzReportView.enum';
import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import type { IBaseReportRecord } from '@/Interfaces/Attribution/ReportRecord.interface';
import type { IGeoJson } from '@/Interfaces/Geo/GeoJson.interface';
import type {
  IBaseDataReport,
  ICampaignReport,
  IDateReport,
  IZipReport,
} from '@/Interfaces/ReportingDashboard/Adswizz/Report.interface';
import type {
  IAdvertiserReportRecord,
  ICampaignNameReportRecord,
  ICreativeReportRecord,
  IDaypartReportRecord,
  IDealReportRecord,
  IDeviceReportRecord,
  IDmaReportRecord,
  IGenreReportRecord,
  IPublisherReportRecord,
} from '@/Interfaces/ReportingDashboard/Adswizz/ReportRecord.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Adswizz/State.interface';
import { GEO_BOUNDARIES, ZIPCODE_ENDPOINT } from '@/constants/endpoints';
import { MAP_SCALES, borderIOHeaders } from '@/constants/geo';
import { adswizzDefaultTotals } from '@/constants/reportingDashboard/adswizz';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { EMPTY_FEATURE_COLLECTION, isPostableBoundariesBody } from '@/helpers/boundaries';
import { getBoundariesRequestBody, getCongressionalBoundariesRequestBody } from '@/helpers/geo';
import { adswizzReportRequestHandler } from '@/helpers/reportingDashboard/adswizz';
import type { RootState } from '@/store';
import type { AdswizzReportingView } from '@/types/ReportingDashboard/Adswizz.types';
import type { RequestMap } from '@/types/RequestMap.types';
import { type AsyncThunk, type AsyncThunkOptions, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

enum ActionPrefix {
  DateReport = 'reportingDashboardAdswizz/getDateReport',
  DeviceReport = 'reportingDashboardAdswizz/getDeviceReport',
  CampaignReport = 'reportingDashboardAdswizz/getCampaignReport',
  OrderReport = 'reportingDashboardAdswizz/getOrderReport',
  AdvertiserReport = 'reportingDashboardAdswizz/getAdvertiserReport',
  CreativeReport = 'reportingDashboardAdswizz/getCreativeReport',
  PublisherReport = 'reportingDashboardAdswizz/getPublisherReport',
  DaypartReport = 'reportingDashboardAdswizz/getDaypartReport',
  GenreReport = 'reportingDashboardAdswizz/getGenreReport',
  DealsReport = 'reportingDashboardAdswizz/getDealsReport',
  DmaReport = 'reportingDashboardAdswizz/getDmaReport',
  ZipReport = 'reportingDashboardAdswizz/getZipReport',
  ZipBoundaries = 'reportingDashboardAdswizz/getZipBoundaries',
  StateBoundaries = 'reportingDashboardAdswizz/getStateBoundaries',
  CountyBoundaries = 'reportingDashboardAdswizz/getCountyBoundaries',
  DmaBoundaries = 'reportingDashboardAdswizz/getDmaBoundaries',
  CongressionalDistrictBoundaries = 'reportingDashboardAdswizz/getCongressionalDistrictBoundaries',
}

const shouldFetchExplicitReport = (report: IBaseReportRecord[], requestId: string | null) => {
  if (requestId === DEFAULT_LOADING_REQUEST_ID) {
    return true;
  }
  return !report?.length && !requestId;
};

const getExplicitReportThunkOptions = (
  dataField: keyof IState,
  requestIdField?: keyof IState,
): AsyncThunkOptions<IAbortSignalThunkArg> => ({
  condition: (_, { getState }) => {
    const requestIdKey = requestIdField ?? (`${dataField}RequestId` as keyof IState);
    const state = getState() as RootState;
    const adswizzState = state.reportingDashboardAdswizz as unknown as IState;
    const data = adswizzState[dataField];
    const requestId = adswizzState[requestIdKey];

    return shouldFetchExplicitReport(data as unknown as IBaseReportRecord[], requestId as string);
  },
});

//#region report thunks
export const getDateReport = createAsyncThunk<IDateReport, IAbortSignalThunkArg>(
  ActionPrefix.DateReport,
  adswizzReportRequestHandler(AdswizzReportType.Date, {
    report: [],
    dayOfWeekReport: [],
    channelReport: [],
    totals: adswizzDefaultTotals,
  }),
  getExplicitReportThunkOptions('dateReport'),
);

export const getDeviceReport = createAsyncThunk<IBaseDataReport<IDeviceReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DeviceReport,
  adswizzReportRequestHandler(AdswizzReportType.Device),
  getExplicitReportThunkOptions('deviceReport'),
);

export const getCampaignReport = createAsyncThunk<ICampaignReport, IAbortSignalThunkArg>(
  ActionPrefix.CampaignReport,
  adswizzReportRequestHandler(AdswizzReportType.Campaign),
  getExplicitReportThunkOptions('campaignReport'),
);

export const getOrderReport = createAsyncThunk<IBaseDataReport<ICampaignNameReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.OrderReport,
  adswizzReportRequestHandler(AdswizzReportType.Order),
  getExplicitReportThunkOptions('orderReport'),
);

export const getAdvertiserReport = createAsyncThunk<IBaseDataReport<IAdvertiserReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.AdvertiserReport,
  adswizzReportRequestHandler(AdswizzReportType.Advertiser),
  getExplicitReportThunkOptions('advertiserReport'),
);

export const getCreativeReport = createAsyncThunk<IBaseDataReport<ICreativeReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.CreativeReport,
  adswizzReportRequestHandler(AdswizzReportType.Creative),
  getExplicitReportThunkOptions('creativeReport'),
);

export const getPublisherReport = createAsyncThunk<IBaseDataReport<IPublisherReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.PublisherReport,
  adswizzReportRequestHandler(AdswizzReportType.Publisher),
  getExplicitReportThunkOptions('publisherNameReport'),
);

export const getDaypartReport = createAsyncThunk<IBaseDataReport<IDaypartReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DaypartReport,
  adswizzReportRequestHandler(AdswizzReportType.Daypart),
  getExplicitReportThunkOptions('dayTimeReport'),
);

export const getGenreReport = createAsyncThunk<IBaseDataReport<IGenreReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.GenreReport,
  adswizzReportRequestHandler(AdswizzReportType.Genre),
  getExplicitReportThunkOptions('genreReport'),
);

export const getDealsReport = createAsyncThunk<IBaseDataReport<IDealReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DealsReport,
  adswizzReportRequestHandler(AdswizzReportType.Deals),
  getExplicitReportThunkOptions('dealsReport'),
);
//#region geo reports
export const getDmaReport = createAsyncThunk<IBaseDataReport<IDmaReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.DmaReport,
  adswizzReportRequestHandler(AdswizzReportType.Dma),
  getExplicitReportThunkOptions('dmaReport'),
);

export const getZipReport = createAsyncThunk<IZipReport, IAbortSignalThunkArg>(
  ActionPrefix.ZipReport,
  adswizzReportRequestHandler(AdswizzReportType.Zip, {
    congressionalDistrictReport: [],
    countyReport: [],
    report: [],
    stateReport: [],
  }),
  getExplicitReportThunkOptions('zipReport'),
);
//#endregion

//#endregion

//#region boundary thunks
export const getZipBoundaries = createAsyncThunk<IGeoJson, { zipCodes: string[] } & IAbortSignalThunkArg>(
  ActionPrefix.ZipBoundaries,
  async payload => {
    const { zipCodes = [], signal } = payload ?? {};
    if (!zipCodes.length) {
      return { type: 'FeatureCollection', features: [] } as IGeoJson;
    }
    const responseResult = await axios.get(ZIPCODE_ENDPOINT, {
      params: { zipcode: zipCodes.join(',') },
      headers: borderIOHeaders,
      signal,
    });
    return responseResult.data;
  },
);

export const getStateBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.StateBoundaries,
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const adswizzState = state.reportingDashboardAdswizz as unknown as IState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(MAP_SCALES.STATE, adswizzState.stateReport, ReportProperty.GEO_STATE);
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, { signal });
    return responseResult.data;
  },
);

export const getCountyBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.CountyBoundaries,
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const adswizzState = state.reportingDashboardAdswizz as unknown as IState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(
      MAP_SCALES.COUNTY,
      adswizzState.countyReport,
      ReportProperty.GEO_COUNTY,
      250,
    );
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, { signal });
    return responseResult.data;
  },
);

export const getDmaBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.DmaBoundaries,
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const adswizzState = state.reportingDashboardAdswizz as unknown as IState;
    const { signal } = payload || {};
    const requestBody = getBoundariesRequestBody(MAP_SCALES.DMA, adswizzState.dmaReport, ReportProperty.DMA_CODE);
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, { signal });
    return responseResult.data;
  },
);

export const getCongressionalDistrictBoundaries = createAsyncThunk<IGeoJson, IAbortSignalThunkArg>(
  ActionPrefix.CongressionalDistrictBoundaries,
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const adswizzState = state.reportingDashboardAdswizz as unknown as IState;
    const { signal } = payload || {};
    const requestBody = getCongressionalBoundariesRequestBody(
      MAP_SCALES.CONGRESSIONAL_DISTRICTS,
      adswizzState.congressionalDistrictReport,
      200,
    );
    if (!isPostableBoundariesBody(requestBody)) return EMPTY_FEATURE_COLLECTION;
    const responseResult = await axios.post(GEO_BOUNDARIES, requestBody, { signal });
    return responseResult.data;
  },
);
//#endregion

//#region maps
export const reportMap: RequestMap<IState> = [
  ['deviceReport', getDeviceReport],
  ['orderReport', getOrderReport],
  ['advertiserReport', getAdvertiserReport],
  ['creativeReport', getCreativeReport],
  ['publisherNameReport', getPublisherReport],
  ['dayTimeReport', getDaypartReport],
  ['genreReport', getGenreReport],
  ['dealsReport', getDealsReport],
  ['dmaReport', getDmaReport],
];

export const boundaryMap: RequestMap<IState> = [
  ['zipBoundaries', getZipBoundaries],
  ['stateBoundaries', getStateBoundaries],
  ['countyBoundaries', getCountyBoundaries],
  ['congressionalDistrictBoundaries', getCongressionalDistrictBoundaries],
  ['dmaBoundaries', getDmaBoundaries],
];
//#endregion

type ActionSet = { action: AsyncThunk<any, any, any>; reportRequest: AdswizzReportRequest };

export const reportDispatch: Record<AdswizzReportingView, ActionSet> = {
  [ReportView.Date]: { action: getDateReport, reportRequest: AdswizzReportRequest.Date },
  [ReportView.Device]: { action: getDeviceReport, reportRequest: AdswizzReportRequest.DeviceType },
  [ReportView.Campaign]: { action: getOrderReport, reportRequest: AdswizzReportRequest.Order },
  [ReportView.Placement]: { action: getCampaignReport, reportRequest: AdswizzReportRequest.Campaign },
  [ReportView.Advertiser]: { action: getAdvertiserReport, reportRequest: AdswizzReportRequest.Advertiser },
  [ReportView.Creative]: { action: getCreativeReport, reportRequest: AdswizzReportRequest.Creative },
  [ReportView.Publisher]: { action: getPublisherReport, reportRequest: AdswizzReportRequest.Publisher },
  [ReportView.DaysOfWeek]: { action: getDateReport, reportRequest: AdswizzReportRequest.Date },
  [ReportView.Daypart]: { action: getDaypartReport, reportRequest: AdswizzReportRequest.DayTime },
  [AdswizzReportView.Genre]: { action: getGenreReport, reportRequest: AdswizzReportRequest.Genre },
  [ReportView.Deals]: { action: getDealsReport, reportRequest: AdswizzReportRequest.Deals },
  [MapScale.Dma]: { action: getDmaReport, reportRequest: AdswizzReportRequest.DmaGeo },
  [MapScale.Zip]: { action: getZipReport, reportRequest: AdswizzReportRequest.ZipGeo },
  [MapScale.County]: { action: getZipReport, reportRequest: AdswizzReportRequest.ZipGeo },
  [MapScale.State]: { action: getZipReport, reportRequest: AdswizzReportRequest.ZipGeo },
  [MapScale.CongressionalDistricts]: { action: getZipReport, reportRequest: AdswizzReportRequest.ZipGeo },
};
